import { AlertTriangle } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { useDeleteRole } from '../hooks/use-roles'
import { useTranslation } from 'react-i18next'
import type { Role } from '../types'

interface DeleteRoleDialogProps {
  role: Role | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DeleteRoleDialog({
  role,
  open,
  onOpenChange,
}: DeleteRoleDialogProps) {
  const { t } = useTranslation()
  const deleteRole = useDeleteRole()

  const handleDelete = async () => {
    if (!role) return

    try {
      await deleteRole.mutateAsync(role.id)
      onOpenChange(false)
    } catch (_error) {
      // Error is handled by mutation hook
    }
  }

  if (!role) return null

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      handleConfirm={handleDelete}
      disabled={deleteRole.isPending}
      title={
        <span className='text-destructive'>
          <AlertTriangle
            className='stroke-destructive me-1 inline-block'
            size={18}
          />{' '}
          {t('roles.dialog.delete.title', 'Delete Role')}
        </span>
      }
      desc={
        <div className='space-y-4'>
          <p>
            {t('roles.dialog.delete.confirmText', { roleName: role.name })}
            <br />
            {t('roles.dialog.delete.warningText', 'This action will permanently remove this role and all its associated permissions from the system. This cannot be undone.')}
          </p>

          <Alert variant='destructive'>
            <AlertTitle>{t('common.warning', 'Warning!')}</AlertTitle>
            <AlertDescription>
              {t('roles.dialog.delete.riskText', 'This operation cannot be rolled back.')}
            </AlertDescription>
          </Alert>
        </div>
      }
      confirmText={deleteRole.isPending ? t('common.deleting', 'Deleting...') : t('common.delete', 'Delete')}
      destructive
    />
  )
}
